// frontend/src/components/postproduction/MissingReports.tsx
import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';

interface MissingReport {
  _id: string;
  name: string;
  date: string;
  venue: {
    _id: string;
    name: string;
  };
  genre: string | null;
}

interface MissingReportsProps {
  missingReports: MissingReport[];
}

const MissingReports: React.FC<MissingReportsProps> = ({ missingReports }) => {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getDaysAgo = (dateString: string) => {
    const showDate = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - showDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleCreateReport = (performanceId: string) => {
    navigate(`/admin/performances/post-production/${performanceId}`);
  };

  if (missingReports.length === 0) {
    return null; 
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          Missing Post-Production Reports
        </Typography>
        <Chip 
          label={missingReports.length} 
          color="warning" 
          size="small"
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        These performances have passed but don't have post-production reports filed yet. 
        Click "Create Report" to add missing data.
      </Alert>

      <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Artist</TableCell>
              <TableCell>Venue</TableCell>
              <TableCell>Genre</TableCell>
              <TableCell align="right">Days Ago</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {missingReports.map((report) => {
              const daysAgo = getDaysAgo(report.date);
              const isOverdue = daysAgo > 7;

              return (
                <TableRow 
                  key={report._id}
                  hover
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: isOverdue ? 'rgba(255, 152, 0, 0.08)' : 'inherit'
                  }}
                >
                  <TableCell>{formatDate(report.date)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {report.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {report.venue.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {report.genre ? (
                      <Typography variant="caption" color="text.secondary">
                        {report.genre}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        Not specified
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${daysAgo} days`}
                      color={isOverdue ? 'error' : 'default'}
                      size="small"
                      variant={isOverdue ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateReport(report._id)}
                      color={isOverdue ? 'error' : 'primary'}
                    >
                      Create Report
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          {missingReports.filter(r => getDaysAgo(r.date) > 7).length} overdue (7+ days) • 
          {' '}{missingReports.length} total missing reports
        </Typography>
      </Box>
    </Box>
  );
};

export default MissingReports;